/**
 * Range slider facet — dual-handle slider UI that submits as discrete checkboxes.
 *
 * Why discrete: Shopify's .gte/.lte URL params don't work for list-type metafield
 * filters, so we can't filter by a continuous range server-side. Instead, on commit,
 * JS checks every hidden checkbox whose discrete numeric value falls within the
 * user's selected slider range. Shopify filters on that list of values.
 *
 * The slider thumb positions will snap to the min/max of the matched discrete values
 * after each AJAX re-render. This is an accepted limitation — see README.
 */

class RangeSliderFacet extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialised === 'true') return;
    this.dataset.initialised = 'true';

    this.minAvail = Number(this.dataset.minAvailable);
    this.maxAvail = Number(this.dataset.maxAvailable);

    this.minThumb = this.querySelector('.range-slider-facet__thumb--min');
    this.maxThumb = this.querySelector('.range-slider-facet__thumb--max');
    this.minInput = this.querySelector('.range-slider-facet__number--min');
    this.maxInput = this.querySelector('.range-slider-facet__number--max');
    this.rangeBar = this.querySelector('.range-slider-facet__range');
    this.hiddenBoxes = Array.from(
      this.querySelectorAll('[data-range-slider-hidden-inputs] input[type="checkbox"]')
    );

    if (!this.minThumb || !this.maxThumb || !this.hiddenBoxes.length) return;

    this.minThumb.addEventListener('input', () => this.onThumbInput('min'));
    this.maxThumb.addEventListener('input', () => this.onThumbInput('max'));
    this.minThumb.addEventListener('change', () => this.commit());
    this.maxThumb.addEventListener('change', () => this.commit());

    this.minInput.addEventListener('change', () => this.onNumberInputChange('min'));
    this.maxInput.addEventListener('change', () => this.onNumberInputChange('max'));

    this.updateRangeBar();
  }

  onThumbInput(which) {
    let lo = Number(this.minThumb.value);
    let hi = Number(this.maxThumb.value);

    if (lo > hi) {
      if (which === 'min') { hi = lo; this.maxThumb.value = hi; }
      else { lo = hi; this.minThumb.value = lo; }
    }

    this.minInput.value = lo;
    this.maxInput.value = hi;
    this.updateRangeBar();
  }

  onNumberInputChange(which) {
    let lo = Number(this.minInput.value);
    let hi = Number(this.maxInput.value);

    if (isNaN(lo)) lo = this.minAvail;
    if (isNaN(hi)) hi = this.maxAvail;

    lo = Math.max(this.minAvail, Math.min(this.maxAvail, lo));
    hi = Math.max(this.minAvail, Math.min(this.maxAvail, hi));

    if (lo > hi) { if (which === 'min') hi = lo; else lo = hi; }

    this.minInput.value = lo;
    this.maxInput.value = hi;
    this.minThumb.value = lo;
    this.maxThumb.value = hi;
    this.updateRangeBar();
    this.commit();
  }

  updateRangeBar() {
    const lo = Number(this.minThumb.value);
    const hi = Number(this.maxThumb.value);
    const span = this.maxAvail - this.minAvail || 1;
    const loPct = ((lo - this.minAvail) / span) * 100;
    const hiPct = ((hi - this.minAvail) / span) * 100;
    this.rangeBar.style.left = loPct + '%';
    this.rangeBar.style.right = (100 - hiPct) + '%';
  }

  commit() {
    const lo = Number(this.minThumb.value);
    const hi = Number(this.maxThumb.value);
    const isFullRange = lo === this.minAvail && hi === this.maxAvail;

    // Check each discrete checkbox if its value falls within the selected range.
    // Full range = uncheck everything (no filter applied).
    let lastChanged = null;
    this.hiddenBoxes.forEach(box => {
      const v = Number(box.dataset.numericValue);
      const shouldBeChecked = !isFullRange && v >= lo && v <= hi;
      if (box.checked !== shouldBeChecked) {
        box.checked = shouldBeChecked;
        lastChanged = box;
      }
    });

    if (!lastChanged && !isFullRange) return;

    // Dispatch bubbling change event — facet-inputs-component picks it up
    const trigger = lastChanged || this.hiddenBoxes[0];
    trigger.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('range-slider-facet')) {
  customElements.define('range-slider-facet', RangeSliderFacet);
}
